// src/pages/public/ContactUs.jsx
import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import {
  Mail,
  MessageCircle,
  Clock,
  Check,
  ArrowRight,
  Send,
  User,
  AlertCircle,
  Phone,
  MapPin,
  HeadphonesIcon
} from 'lucide-react'
import { api } from '../../api/client'
import { endpoints } from '../../api/endpoints'

const ContactUs = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { register, handleSubmit, formState: { errors }, reset } = useForm()

  const contactInfo = [
    {
      icon: Mail,
      title: "Email Support",
      details: "support@tgfuture.com",
      description: "Comprehensive support via email",
      link: "mailto:support@tgfuture.com",
      color: "from-[#9E1C60] to-[#811844]"
    },
    {
      icon: MessageCircle,
      title: "Telegram Support",
      details: "@TGFutureTFG1E4",
      description: "Instant messaging support",
      link: "https://t.me/TGFutureTFG1E4",
      color: "from-[#F5AD18] to-[#9E1C60]"
    },
    {
      icon: Clock,
      title: "Response Time",
      details: "Under 2 Hours",
      description: "Average response time",
      color: "from-[#811844] to-[#561530]"
    },
    {
      icon: HeadphonesIcon,
      title: "24/7 Support",
      details: "Always Available",
      description: "Round-the-clock assistance",
      color: "from-[#561530] to-[#9E1C60]"
    }
  ]

  const features = [
    {
      icon: Check,
      title: "Expert Investment Advice",
      description: "Get personalized guidance from our financial experts"
    },
    {
      icon: Check,
      title: "Technical Support",
      description: "Quick resolution for platform-related issues"
    },
    {
      icon: Check,
      title: "Account Management",
      description: "Assistance with deposits, withdrawals, and account setup"
    },
    {
      icon: Check,
      title: "Security Guidance",
      description: "Help with security best practices and account protection"
    }
  ]

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    try {
      await api.post(endpoints.public.contact, data)
      toast.success('Message sent successfully! We will get back to you soon.')
      reset()
    } catch (error) {
      toast.error('Failed to send message. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#561530] via-[#811844] to-[#9E1C60] text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Contact{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F5AD18] to-[#9E1C60]">
                Support
              </span>
            </h1>
            <p className="text-xl text-[#F5AD18] mb-8 leading-relaxed">
              Get in touch with our expert team for investment guidance, technical support, 
              or any questions about our platform.
            </p>

            {/* <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="mailto:support@TGFuture.com"
                className="bg-[#F5AD18] hover:bg-[#F5AD18]/90 text-[#561530] px-8 py-4 rounded-lg font-semibold text-lg inline-flex items-center gap-3 transition-all transform hover:scale-105 shadow-lg"
              >
                <Mail className="w-6 h-6" />
                Email Support
                <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href="https://t.me/TGFutureYTE4"
                target="_blank"
                rel="noopener noreferrer"
                className="border-2 border-white text-white hover:bg-white hover:text-[#561530] px-8 py-4 rounded-lg font-semibold text-lg inline-flex items-center gap-3 transition-all"
              >
                <MessageCircle className="w-6 h-6" />
                Telegram Chat
              </a>
            </div> */}
          </motion.div>
        </div>
      </section>

      {/* Features Banner */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center group"
              >
                <div className={`bg-gradient-to-r ${info.color} w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  <info.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                  {info.title}
                </h3>
                {info.link ? (
                  <a
                    href={info.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-900 dark:text-white font-semibold hover:text-[#9E1C60] dark:hover:text-[#F5AD18] transition-colors block mb-2"
                  >
                    {info.details}
                  </a>
                ) : (
                  <p className="text-gray-900 dark:text-white font-semibold mb-2">
                    {info.details}
                  </p>
                )}
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  {info.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
                  We're Here to Help
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
                  Our dedicated support team is ready to assist you with any questions about 
                  investment strategies, platform features, or account management.
                </p>
              </div>

              {/* Support Features */}
              <div className="grid gap-6">
                {features.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700"
                  >
                    <div className="bg-gradient-to-r from-[#9E1C60] to-[#811844] w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">
                        {feature.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Additional Info */}
              <div className="bg-gradient-to-r from-[#561530] to-[#811844] rounded-2xl p-6 text-white">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-3">
                  <HeadphonesIcon className="w-6 h-6" />
                  Premium Support Benefits
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-center space-x-3">
                    <Check className="w-5 h-5 text-[#F5AD18]" />
                    <span>Priority response for all inquiries</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <Check className="w-5 h-5 text-[#F5AD18]" />
                    <span>Multilingual support team</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <Check className="w-5 h-5 text-[#F5AD18]" />
                    <span>Direct access to investment experts</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <Check className="w-5 h-5 text-[#F5AD18]" />
                    <span>Personalized investment guidance</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700"
            >
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Send us a Message
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Fill out the form below and we'll get back to you within 2 hours
              </p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Full Name *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        {...register("name", { required: "Name is required" })}
                        type="text"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-[#9E1C60] focus:border-[#9E1C60] dark:bg-gray-700 dark:text-white transition-colors"
                        placeholder="Enter your full name"
                      />
                    </div>
                    {errors.name && (
                      <p className="text-red-500 text-sm mt-1 flex items-center space-x-1">
                        <AlertCircle className="w-4 h-4" />
                        <span>{errors.name.message}</span>
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email Address *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        {...register("email", {
                          required: "Email is required",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address"
                          }
                        })}
                        type="email"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-[#9E1C60] focus:border-[#9E1C60] dark:bg-gray-700 dark:text-white transition-colors"
                        placeholder="Enter your email"
                      />
                    </div>
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1 flex items-center space-x-1">
                        <AlertCircle className="w-4 h-4" />
                        <span>{errors.email.message}</span>
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Subject *
                  </label>
                  <select
                    {...register("subject", { required: "Subject is required" })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-[#9E1C60] focus:border-[#9E1C60] dark:bg-gray-700 dark:text-white transition-colors"
                  >
                    <option value="">Select a subject</option>
                    <option value="GENERAL">General Inquiry</option>
                    <option value="INVESTMENT">Investment Questions</option>
                    <option value="TECHNICAL">Technical Support</option>
                    <option value="WITHDRAWAL">Withdrawal Issues</option>
                    <option value="PARTNERSHIP">Partnership</option>
                    <option value="OTHER">Other</option>
                  </select>
                  {errors.subject && (
                    <p className="text-red-500 text-sm mt-1 flex items-center space-x-1">
                      <AlertCircle className="w-4 h-4" />
                      <span>{errors.subject.message}</span>
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Message *
                  </label>
                  <div className="relative">
                    <MessageCircle className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                    <textarea
                      {...register("message", {
                        required: "Message is required",
                        minLength: {
                          value: 10,
                          message: "Message must be at least 10 characters"
                        }
                      })}
                      rows={6}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-[#9E1C60] focus:border-[#9E1C60] dark:bg-gray-700 dark:text-white transition-colors resize-none"
                      placeholder="Tell us how we can help you..."
                    />
                  </div>
                  {errors.message && (
                    <p className="text-red-500 text-sm mt-1 flex items-center space-x-1">
                      <AlertCircle className="w-4 h-4" />
                      <span>{errors.message.message}</span>
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#561530] to-[#9E1C60] hover:from-[#811844] hover:to-[#9E1C60] text-white font-semibold py-4 px-6 rounded-xl transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2 shadow-lg"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sending Message...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>Send Message</span>
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Quick answers to common questions
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                question: "How quickly will I get a response?",
                answer: "We typically respond within 2 hours during business hours, and within 6 hours for after-hours inquiries."
              },
              {
                question: "What information should I include in my message?",
                answer: "Please include your account email, specific issue details, and any relevant transaction IDs for faster resolution."
              },
              {
                question: "Do you offer phone support?",
                answer: "Currently, we provide support via email and Telegram for the fastest and most efficient service."
              },
              {
                question: "Can I get investment advice?",
                answer: "Yes! Our investment experts can provide guidance on package selection and strategy based on your goals."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-gray-700 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-600"
              >
                <h3 className="font-bold text-gray-900 dark:text-white mb-3 text-lg">
                  {faq.question}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <a
              href="/faq"
              className="text-[#9E1C60] dark:text-[#F5AD18] hover:text-[#811844] dark:hover:text-[#F5AD18]/80 font-semibold inline-flex items-center space-x-2"
            >
              <span>View All FAQs</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default ContactUs